<template>
  <div class="new-style select-retained-earnings-account">
    <div class="sf-below-2 sf-row">
      <div class="sf-col">
        <SfSelect2
          ref="select2Wrapper"
          v-model="currentRetainedEarningsAccountNumber"
          :configuration="select2_configuration"
        ></SfSelect2>
      </div>
      <div v-if="currentRetainedEarningsAccountNumber" class="sf-col">
        <input
          v-model="currentRetainedEarningsAccountName"
          :disabled="!isAddingNew"
          autocomplete="off"
          autofocus="true"
          class="sf-form-input sf-full"
          placeholder="Enter new account name"
          type="text"
        />
      </div>
    </div>
  </div>
</template>

<script>
import sanitizer from '@app/adapters/sanitizer'
import SfSelect2 from '@app/shared/vue/components/form/sf-select2'
import formatAccountsForSelect2 from '@app/utils/format-accounts-for-select2'

export default {
  components: {
    SfSelect2,
  },
  props: {
    accountNumberFormSelector: {
      type: String,
      default: '#retained_earnings_account_number',
    },
    accountNameFormSelector: {
      type: String,
      default: '#retained_earnings_account_name',
    },
    selectedRetainedEarningsAccount: {
      type: Object,
      default() {
        return {
          name: '',
          number: '',
        }
      },
    },
  },

  data() {
    const doNotBringForwardOption = {
      text: I18n.t(
        'ledger_import.bring_forward_retained_earnings.do_not_bring_forward_automatically'
      ),
      id: '',
    }

    return {
      initialized: false,
      isAddingNew: false,
      currentRetainedEarningsAccountNumber:
        this.selectedRetainedEarningsAccount?.number ?? '',
      currentRetainedEarningsAccountName:
        this.selectedRetainedEarningsAccount?.name ?? '',
      select2_configuration: {
        selectOnClose: true,
        placeholder: doNotBringForwardOption,
        allowClear: true,
        minimumInputLength: 1,
        width: '100%',
        theme: 'silverfin',
        dropdownCssClass: 'accounts-dropdown',
        escapeMarkup(markup) {
          return sanitizer(markup)
        },
        templateResult(item) {
          if (item.children) {
            // it's a section
            return item.text
          } else {
            // it's an account
            return item.formatted_text
          }
        },
        templateSelection(account) {
          if (account.id) {
            return account.id
          } else {
            return account.text
          }
        },
        ajax: {
          url: Silverfin.Routes.autocomplete_accounts_path({
            firm_id: Silverfin.currentFirmId,
            company_id: Silverfin.currentCompanyId,
            filter_account_mappings: true, // Filters accout mappings suggestions. See https://gitlab.silverfin.com/development/silverfin/-/issues/11794
          }),
          dataType: 'json',
          data(params) {
            const query = {
              q: params.term,
              equity_or_liability: true,
            }
            return query
          },
          processResults(data) {
            return { results: data.map(formatAccountsForSelect2) }
          },
        },
      },
    }
  },

  watch: {
    currentRetainedEarningsAccountName: function (newVal, _oldVal) {
      this.updateNameHiddenInput(newVal)
    },
    currentRetainedEarningsAccountNumber: function (newVal, _oldVal) {
      this.updateNumberHiddenInput(newVal)
    },
  },

  mounted() {
    const $select2 = window.$(this.$refs.select2Wrapper.$refs.select2)

    const { name, number } = this.selectedRetainedEarningsAccount

    // change the retainedEarningsAccount when the selection changes
    $select2.on('select2:select', (e) => {
      const data = e.params.data

      if (!data.name) {
        this.isAddingNew = true
      }

      this.currentRetainedEarningsAccountName = data.name
      this.currentRetainedEarningsAccountNumber = data.value
    })

    // close the dropdown when hitting select
    // as it's messing up with the partial update otherwise
    $select2.on('select2:unselecting', (_e) => {
      setTimeout(() => $select2.select2('close'), 0)
    })

    // Set currently selected reatined earnings account
    if (name || number) {
      const label = `${name} ${number}`
      // create the option and append to Select2
      const data = {
        formatted_text: label,
        id: number,
      }
      const option = new Option(data.formatted_text, data.id, true, true)
      $select2.append(option).trigger('change')

      // manually trigger the `select2:select` event
      $select2.trigger({
        type: 'select2:select',
        params: {
          data: {
            name,
            number,
          },
        },
      })

      this.currentRetainedEarningsAccountName = name
      this.currentRetainedEarningsAccountNumber = number

      if (name) {
        this.updateNameHiddenInput(name)
      }

      if (number) {
        this.updateNumberHiddenInput(number)
      }
    }

    this.initialized = true
  },

  methods: {
    dispatchChangeEvent: function (input) {
      if (this.initialized) {
        $(input).trigger('change')
      }
    },
    updateNameHiddenInput: function (name) {
      const input = document.querySelector(this.accountNameFormSelector)
      if (input) {
        input.value = name
        this.dispatchChangeEvent(input)
      } else {
        console.warn(
          `Couldn't find the input for updating: ${this.accountNameFormSelector}`
        )
      }
    },
    updateNumberHiddenInput: function (number) {
      const input = document.querySelector(this.accountNumberFormSelector)
      if (input) {
        input.value = number
        this.dispatchChangeEvent(input)
      } else {
        console.warn(
          `Couldn't find the input for updating: ${this.accountNumberFormSelector}`
        )
      }
    },
  },
}
</script>
